/* eslint-disable max-len */
import { Redirect } from 'react-router-dom';

import { RouteParams } from '@/routing/Router';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import Home from '@/pages/Home/Home.page';
import AddContractLines from '@/pages/AddContractLines/AddContractLines.page';
import ContractDetails from '@/pages/ContractDetails/ContractDetails.page';
import AddContract from '@/pages/AddContract/AddContract.page';
import SystemAdmin from '@/pages/SystemAdmin/SystemAdmin.page';
import UpliftsManager from '@/pages/UpliftsManager/UpliftsManager.page';
import NotAuthorized from '@/pages/NotAuthorized/NotAuthorized';
import NotFound from '@/pages/NotFound/NotFound';
import ContractDetailsBreadcrumb from '@/routing/breadcrumbs/ContractDetails.breadcrumb';
import GridFilteredBySearchBreadcrumb, {
  SearchBreadcrumbPageNames,
} from '@/routing/breadcrumbs/GridFilteredBySearch.breadcrumb';
import NewContractsFilteredByCustomerBreadcrumb from '@/routing/breadcrumbs/NewContractsFilteredByCustomer.breadcrumb';
import GridFilteredBySavedSearch from '@/routing/breadcrumbs/GridFilteredBySavedSearch.breadcrumb';
import FileDetails from '@/pages/ImportingTool/FileDetails/FileDetails';
import FileDetailsPageBreadcrumb from '@/routing/breadcrumbs/FileDetails.breadcrumb';
import BillingSalesOrderIdBreadcrumb from '@/routing/breadcrumbs/BillingSalesOrderId.breadcrumb';
import UpliftRates from '@/pages/UpliftRates/UpliftRates.page';
import AddVariableContractLines from '@/pages/AddContractLines/VariableContractLines/AddVariableContractLines.page';
import GenericFilteredBy from '@/routing/breadcrumbs/GenericFilteredBy.breadcrumb';
import BillingBCFilteredByBreadcrumb from '@/routing/breadcrumbs/BillingBCFilteredBy.breadcrumb';
import ClientDatabases from '@/pages/ClientDatabases/ClientDatabasesModule';
import BundlesModule from '@/pages/Bundles/BundlesModule';
import ContractLinesModule from '@/pages/ContractLines/ContractLinesModule';
import ContractsModule from '@/pages/Contracts/ContractsModule';
import ContractLinesFilteredByCustomerBreadcrumb
  from '@/routing/breadcrumbs/ContractLinesFilteredByCustomer.breadcrumb';
import PurchaseOrdersModule from '@/pages/PurchaseOrders/PurchaseOrdersModule';
import SalesOrdersModule from '@/pages/SalesOrders/SalesOrdersModule';
import BillingCyclesModule from '@/pages/BillingCycles/BillingCyclesModule';
import CustomersModule from '@/pages/Customers/CustomersModule';
import ProductsModule from '@/pages/Products/ProductsModule';
import UpliftsHistoryModule from '@/pages/UpliftsHistory/UpliftsHistoryModule';
import VarBillingModule from '@/pages/VarBilling/VarBillingModule';

export const routes: RouteParams[] = [
  {
    path: '/',
    title: 'Home Page',
    Component: Home,
    exact: true,
  },
  {
    path: '/contract-lines',
    Component: ContractLinesModule,
    title: 'Contract Lines',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.ContractLines}
          path={'/contract-lines'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: ContractLinesModule,
        title: 'Contract Lines filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              breadcrumbPage={SearchBreadcrumbPageNames.ContractLines}
              path='/contract-lines/filteredBy'
            />
          );
        },
      },
      {
        path: '/contracts-bc/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/contract-lines/contracts-bc/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/contracts-bc/filteredBy/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/contracts-bc/filteredBy/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ]
  },
  {
    path: '/bundles',
    Component: BundlesModule,
    title: 'Bundles',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Bundles}
          path={'/bundles'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: BundlesModule,
        title: 'Bundles filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/bundles/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Bundles}
            />
          );
        },
      },
      {
        path: '/contracts-bc/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/bundles/contracts-bc/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/bundles/contracts-bc/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/bundles/contracts-bc/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '/contracts-bc',
    Component: ContractsModule,
    title: 'Contracts',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.ContractsBC}
          path={'/contracts-bc'}
        />
      );
    },
    children: [
      {
        exact: true,
        path: '/add-contract',
        Component: AddContract,
        title: 'Add New Contract',
      },
      {
        exact: true,
        path: '/:contractId/add-contract-line',
        Component: AddContractLines,
        title: 'Add New Contract Line',
      },
      {
        exact: true,
        path: '/:contractId/add-variable-contract-line',
        Component: AddVariableContractLines,
        title: 'Add New Variable Contract Line',
      },
      {
        path: '/filteredBy',
        Component: ContractsModule,
        exact: true,
        title: 'Contracts filtered by customer',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/contracts-bc/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.ContractsBC}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/contracts-bc/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/contracts-bc/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/contracts-bc/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/customers',
        Component: ContractsModule,
        exact: true,
        title: 'Contracts filtered by customer',
        get customBreadcrumbItem() {
          return (
            <NewContractsFilteredByCustomerBreadcrumb
              defaultText={this.title}
              path={'/contracts-bc/customers'}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/contracts-bc/customers/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/contracts-bc/customers/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/contracts-bc/customers/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/contracts-bc/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/contracts-bc/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/contracts-bc/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/system-admin',
    Component: SystemAdmin,
    title: 'System Admin',
  },
  {
    path: '/client-databases',
    Component: ClientDatabases,
    title: 'Client Databases',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.ClientDatabases}
          path={'/client-databases'}
        />
      );
    },
  },
  {
    path: '/uplifts-manager',
    Component: UpliftsManager,
    title: 'Uplifts Manager',
  },
  {
    path: '/uplift-rates',
    Component: UpliftRates,
    title: 'Uplift Rates',
    exact: true,
  },
  {
    path: '/billing-bc',
    Component: SalesOrdersModule,
    title: 'Sales Orders',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <BillingBCFilteredByBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
          path={'/billing-bc'}
        />
      );
    },
    children: [
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-bc/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-bc/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-bc/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-bc-failed',
    Component: SalesOrdersModule,
    title: 'Failed Sales Orders',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
          path={'/billing-bc-failed'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-bc-failed/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-bc-failed/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-bc-failed/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-bc-failed/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-bc-failed/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-bc-failed/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-bc-failed/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-bc-paused',
    Component: SalesOrdersModule,
    title: 'Billing Paused Sales Orders',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
          path={'/billing-bc-paused'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-bc-paused/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-bc-paused/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-bc-paused/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-bc-paused/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-bc-paused/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-bc-paused/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-bc-paused/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-bc-scheduled',
    Component: SalesOrdersModule,
    title: 'Scheduled Sales Orders',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
          path={'/billing-bc-scheduled'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-bc-scheduled/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-bc-scheduled/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-bc-scheduled/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-bc-scheduled/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-bc-scheduled/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-bc-scheduled/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-bc-scheduled/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-bc-sent',
    Component: SalesOrdersModule,
    title: 'Sent Sales Orders',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
          path={'/billing-bc-sent'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-bc-sent/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.BillingBC}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-bc-sent/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-bc-sent/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-bc-sent/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-bc-sent/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-bc-sent/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-bc-sent/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-new',
    Component: SalesOrdersModule,
    title: 'Sales Orders (OLD)',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Billing}
          path={'/billing-new'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-new/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Billing}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-new/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-new/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-new/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-new/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-new/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-new/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-new-failed',
    Component: SalesOrdersModule,
    title: 'Failed Sales Orders (OLD)',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Billing}
          path={'/billing-new-failed'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-new-failed/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Billing}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-new-failed/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-new-failed/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-new-failed/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-new-failed/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-new-failed/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-new-failed/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-new-scheduled',
    Component: SalesOrdersModule,
    title: 'Scheduled Sales Orders (OLD)',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Billing}
          path={'/billing-new-scheduled'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-new-scheduled/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Billing}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-new-scheduled/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-new-scheduled/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-new-scheduled/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-new-scheduled/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-new-scheduled/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-new-scheduled/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-new-sent',
    Component: SalesOrdersModule,
    title: 'Sent Sales Orders (OLD)',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Billing}
          path={'/billing-new-sent'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: SalesOrdersModule,
        title: 'Billing filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-new-sent/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Billing}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/billing-new-sent/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/billing-new-sent/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/billing-new-sent/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-new-sent/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-new-sent/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-new-sent/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/customers-new',
    Component: CustomersModule,
    title: 'Customers',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Customers}
          path={'/customers-new'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: CustomersModule,
        title: 'Customers filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/customers-new/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Customers}
            />
          );
        },
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/customers-new/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/customers-new/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/customers-new/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/variable-billing',
    Component: VarBillingModule,
    exact: true,
    title: 'Variable Billing',
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.VariableBilling}
          path={'/variable-billing'}
        />
      );
    },
    children: [
      {
        path: '/:fileId',
        exact: true,
        Component: FileDetails,
        title: 'File Details',
        get customBreadcrumbItem() {
          return (
            <FileDetailsPageBreadcrumb
              defaultText={this.title}
              routes={['/variable-billing/:fileId']}
            />
          );
        },
        children: [
          {
            path: '/billing-new',
            title: 'Sales Orders (OLD)',
            exact: true,
            Component: SalesOrdersModule,
            get customBreadcrumbItem() {
              // TODO: Refactor breadcrumbs to use the same component for many pages
              // We should use the same component for such cases
              // Sales Order ID: <id here>
              // Account Number: <number here>
              return <BillingSalesOrderIdBreadcrumb defaultText='Sales Orders (OLD)' />;
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/variable-billing/:fileId/billing-new/:contractId']}
                    />
                  );
                },
              }
            ]
          },
          {
            path: '/billing-bc',
            title: 'Sales Orders',
            exact: true,
            Component: SalesOrdersModule,
            get customBreadcrumbItem() {
              return <BillingSalesOrderIdBreadcrumb defaultText='Sales Orders' />;
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/variable-billing/:fileId/billing-bc/:contractId']}
                    />
                  );
                },
              }
            ]
          },
          {
            path: '/contracts-bc/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/variable-billing/:fileId/contracts-bc/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/variable-billing/:fileId/contracts-bc/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/variable-billing/:fileId/contracts-bc/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
    ],
  },
  {
    path: '/products',
    Component: ProductsModule,
    title: 'Products',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.Products}
          path={'/products'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        Component: ProductsModule,
        exact: true,
        title: 'Products filtered by ',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/products/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.Products}
            />
          );
        },
      }
    ]
  },
  {
    path: '/uplifts-history',
    Component: UpliftsHistoryModule,
    title: 'Uplifts History',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
          path={'/uplifts-history'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: UpliftsHistoryModule,
        title: 'Uplifts History filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/uplifts-history/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/uplifts-history/filteredBy/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/uplifts-history/filteredBy/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/uplifts-history/filteredBy/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },
      {
        path: '/contracts-bc/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/uplifts-history/contracts-bc/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/uplifts-history/contracts-bc/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/uplifts-history/contracts-bc/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/billing-cycles',
    exact: true,
    Component: BillingCyclesModule,
    title: 'Billing Cycles',
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          breadcrumbPage={SearchBreadcrumbPageNames.BillingCycles}
          path={'/billing-cycles'}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        Component: BillingCyclesModule,
        exact: true,
        title: 'Billing Cycles filtered by ',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/billing-cycles/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.BillingCycles}
            />
          );
        },
        children: [
          {
            path: '/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/contracts-bc/filteredBy/:contractId']}
                />
              );
            },
          }
        ]
      },
      {
        path: '/:contractId',
        exact: true,
        Component: ContractDetails,
        title: 'Contract Details',
        get customBreadcrumbItem() {
          return (
            <ContractDetailsBreadcrumb
              defaultText={this.title}
              routes={['/billing-cycles/:contractId']}
            />
          );
        },
        children: [
          {
            path: '/uplifts-history',
            Component: UpliftsHistoryModule,
            exact: true,
            title: 'Uplifts History filtered by Contract',
            get customBreadcrumbItem() {
              return (
                <GenericFilteredBy
                  defaultText={this.title}
                  path='/billing-cycles/:contractId/uplifts-history'
                  breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                  filteredDisplayName='Contract'
                  filteredSearchParam='contractNumber'
                />
              );
            },
            children: [
              {
                path: '/:contractId',
                exact: true,
                Component: ContractDetails,
                title: 'Contract Details',
                get customBreadcrumbItem() {
                  return (
                    <ContractDetailsBreadcrumb
                      defaultText={this.title}
                      routes={['/billing-cycles/:contractId/uplifts-history/:contractId']}
                    />
                  );
                },
              }
            ]
          }
        ]
      },
    ],
  },
  {
    path: '/purchase-orders',
    Component: PurchaseOrdersModule,
    title: 'Purchase Orders',
    exact: true,
    get customBreadcrumbItem() {
      return (
        <GridFilteredBySearchBreadcrumb
          defaultText={this.title}
          path='/purchase-orders'
          breadcrumbPage={SearchBreadcrumbPageNames.PurchaseOrders}
        />
      );
    },
    children: [
      {
        path: '/filteredBy',
        exact: true,
        Component: PurchaseOrdersModule,
        title: 'Purchase Orders filtered by saved search',
        get customBreadcrumbItem() {
          return (
            <GridFilteredBySavedSearch
              defaultText={this.title}
              path='/purchase-orders/filteredBy'
              breadcrumbPage={SearchBreadcrumbPageNames.PurchaseOrders}
            />
          );
        },
      },
      {
        path: '/contract-lines',
        Component: ContractLinesModule,
        exact: true,
        title: 'Contract Lines filtered test',
        get customBreadcrumbItem() {
          return (
            <ContractLinesFilteredByCustomerBreadcrumb
              defaultText={this.title}
              path={'/purchase-orders/contract-lines'}
            />
          );
        },
        children: [
          {
            path: '/contracts-bc/:contractId',
            exact: true,
            Component: ContractDetails,
            title: 'Contract Details',
            get customBreadcrumbItem() {
              return (
                <ContractDetailsBreadcrumb
                  defaultText={this.title}
                  routes={['/purchase-orders/contract-lines/contracts-bc/:contractId']}
                />
              );
            },
            children: [
              {
                path: '/uplifts-history',
                Component: UpliftsHistoryModule,
                exact: true,
                title: 'Uplifts History filtered by Contract',
                get customBreadcrumbItem() {
                  return (
                    <GenericFilteredBy
                      defaultText={this.title}
                      path='/purchase-orders/contract-lines/contracts-bc/:contractId/uplifts-history'
                      breadcrumbPage={SearchBreadcrumbPageNames.UpliftsHistory}
                      filteredDisplayName='Contract'
                      filteredSearchParam='contractNumber'
                    />
                  );
                },
                children: [
                  {
                    path: '/:contractId',
                    exact: true,
                    Component: ContractDetails,
                    title: 'Contract Details',
                    get customBreadcrumbItem() {
                      return (
                        <ContractDetailsBreadcrumb
                          defaultText={this.title}
                          routes={['/purchase-orders/contract-lines/contracts-bc/:contractId/uplifts-history/:contractId']}
                        />
                      );
                    },
                  }
                ]
              }
            ]
          },
        ],
      },

    ]
  },
  {
    path: '/not-found',
    Component: NotFound,
    title: 'Page Not Found',
  },
  {
    path: '/not-authorized',
    Component: NotAuthorized,
    title: 'Not authorized',
  },
  {
    path: '*',
    Component: () => <Redirect to={getParameterizedLink('/')} />,
    title: 'Redirect...',
  },
];
